import type {
  LibraryAssetContent,
  QuestionnaireContent,
  ScoringMethod,
  ValidityCheck,
} from "@/api/library";

/**
 * The editable projection of a questionnaire, and the rules the browser can
 * enforce about it without duplicating the server.
 *
 * WHY A PROJECTION AT ALL. The stored shape is a finished instrument: every
 * response-scale value is an integer, every band is an ordered pair, every item
 * id is a slug. None of those is a thing a clinician can type INTO — a `3`
 * backspaced to `""` is a state rather than a zero, and a band whose `scoreTo`
 * is mid-keystroke is briefly reversed. The draft therefore carries numbers as
 * text buffers and gives every row a React identity distinct from the id the
 * clinician edits, exactly as `lib/alertMatrix.ts` does. Conversion back to the
 * wire shape happens only on Save.
 *
 * WHAT IS CHECKED HERE, AND WHY THAT IS NOT A SECOND OPINION.
 *
 * Everything below is stated on the WIRE MODEL in `schemas/library.py` —
 * `QuestionnaireContent._coherent`, `SeverityBand._ordered`,
 * `ValidityCheck._unique_indicators` — and each rule is attributable to an
 * exact control on screen. Catching them before the request is a better
 * message, not a competing authority: the server validates again, and its
 * field-pointed 422 is what decides.
 *
 * BAND GAPS ARE A WARNING, NOT A REFUSAL. The service permits interior gaps
 * deliberately — partial banding is legitimate authoring for an instrument that
 * only flags elevated ranges, and the seeded PHQ-9 file says so — so refusing
 * one here would make the editor stricter than the contract and leave a
 * questionnaire authored elsewhere unsaveable. An uncovered range is surfaced
 * the way `findMatrixWarnings` surfaces a privacy hazard: on the control, and
 * without blocking the save. OVERLAP is a hard error, because the server
 * refuses it.
 *
 * SCORING IS TWO OPTIONAL FIELDS AND NOTHING ELSE (#368). `scoringMethod`
 * chooses which derived range the bands are checked against; `reverseScored`
 * re-keys one item. Per-item weights, subscales, skip logic and branching are
 * out of scope, and nothing here scores a respondent's answers — the editor
 * authors instruments, it does not run them.
 */

/** Contract bounds, named so a message can quote the number it enforces. */
export const MIN_ITEMS = 1;
export const MAX_ITEMS = 200;
export const MIN_SCALE_OPTIONS = 2;
export const MAX_SCALE_OPTIONS = 12;
export const MAX_BANDS = 24;
export const MAX_INDICATORS = 200;
/** `BoundedName` — a band's name and its runtime severity label. */
export const MAX_NAME_CHARS = 200;
/** `ShortText` — item wording, option labels, the administration stem. */
export const MAX_TEXT_CHARS = 2000;

/** `SlugKey`, from the contract. Item ids and indicator references share it. */
export const ITEM_ID_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;
/** `QuestionnaireContent.language`: `en` or `en-GB`. */
export const LANGUAGE_PATTERN = /^[a-z]{2}(-[A-Z]{2})?$/;

export const SCORING_METHODS = ["sum", "mean"] as const satisfies
  readonly ScoringMethod[];

export const SCORING_METHOD_LABELS: Record<ScoringMethod, string> = {
  sum: "Total of item responses",
  mean: "Average of item responses",
};

export interface DraftQuestionnaireItem {
  /** React identity, distinct from `itemId`, which the clinician edits. */
  id: string;
  itemId: string;
  text: string;
  /**
   * Re-keys this item: its contribution becomes `(scaleMin + scaleMax) - value`.
   *
   * `false` rather than `false | undefined`, because the draft is what the
   * clinician sees and "not reverse-keyed" is one state, not two. It serialises
   * back to an ABSENT key so an instrument that reverses nothing stores exactly
   * what it stored before this field existed.
   */
  reverseScored: boolean;
}

export interface DraftResponseOption {
  id: string;
  /** Text, not a number: `0` backspaced to `""` is a state, not a zero. */
  value: string;
  label: string;
}

export interface DraftSeverityBand {
  id: string;
  name: string;
  scoreFrom: string;
  scoreTo: string;
  severityLabel: string;
}

export interface DraftValidity {
  /**
   * Whether the instrument carries a validity screen AT ALL — `validity` is
   * nullable in the contract, and its absence is a different fact from a
   * screen that is present and switched off.
   */
  present: boolean;
  /** `ValidityCheck.enabled`: a stored screen that does not currently fire. */
  enabled: boolean;
  threshold: string;
  /** Authored item ids, in the order the clinician selected them. */
  indicatorItemIds: string[];
}

export interface DraftQuestionnaire {
  language: string;
  instructions: string;
  items: DraftQuestionnaireItem[];
  responseScale: DraftResponseOption[];
  scored: boolean;
  scoringMethod: ScoringMethod;
  severityBands: DraftSeverityBand[];
  validity: DraftValidity;
}

/**
 * One refusal or warning, attributable to a control.
 *
 * `path` is the field-anchor path of the control it belongs to, or
 * `"content"` when the problem belongs to the instrument as a whole.
 */
export interface QuestionnaireProblem {
  path: string;
  message: string;
}

let nextDraftId = 0;

function newDraftId(prefix: string): string {
  nextDraftId += 1;
  return `draft-questionnaire-${prefix}-${nextDraftId}`;
}

export function isQuestionnaireContent(
  content: LibraryAssetContent,
): content is QuestionnaireContent {
  return content.kind === "questionnaire";
}

/**
 * How a stored instrument is DESCRIBED, in one place (#368's words, #369's
 * second reader).
 *
 * The rest of this module is the authoring projection; these six are the
 * read-only vocabulary, and they live here because two surfaces now say the
 * same things about one instrument: the library's version viewer
 * (`ui/library/VersionContent.tsx`, which introduced every string below) and a
 * Project's adoption row (`ui/adoptions/QuestionnaireAdoptionDetail.tsx`),
 * which states what the PINNED version administers. Two copies of "Scored —
 * total of item responses" is two places for it to drift, and a clinician
 * reading the library and the Project must not be told the same fact in two
 * different sentences.
 *
 * Every one of them is a statement about STORED CONTENT and nothing else. None
 * scores a respondent, and none consults the library's head — the caller
 * supplies the content it read, and these say what it holds.
 */

/**
 * Scored or not, and how — read the way it is stored.
 *
 * An ABSENT `scoringMethod` reads as the total, because that is what the
 * service documents an absent field to mean, and a questionnaire published
 * before #368 must not read as though somebody chose `sum` for it.
 */
export function scoringSentence(content: QuestionnaireContent): string {
  if (!content.scored) {
    return "Not scored";
  }
  return content.scoringMethod === "mean"
    ? "Scored — average of item responses"
    : "Scored — total of item responses";
}

/** "9 items" — how many the instrument administers. */
export function itemCountSentence(content: QuestionnaireContent): string {
  const count = content.items.length;
  return `${count} ${count === 1 ? "item" : "items"}`;
}

/**
 * How many bands stratify the score, INCLUDING none.
 *
 * "No severity bands" rather than silence: an unbanded instrument is a real
 * configuration — the service permits one deliberately — and a row that simply
 * omitted the fact would read as though the count were unknown.
 */
export function severityBandSentence(content: QuestionnaireContent): string {
  const count = (content.severityBands ?? []).length;
  return count === 0
    ? "No severity bands"
    : `${count} severity ${count === 1 ? "band" : "bands"}`;
}

/**
 * Whether a stored validity screen currently fires.
 *
 * `ValidityCheck.enabled` is a property of the INSTRUMENT VERSION, not of the
 * project that adopts it: the contract says a project adopts the screen with
 * the version and cannot re-point it (#34). So this is read-only wherever it is
 * rendered, and the adoption surface presents it rather than offering a toggle.
 */
export function validityStateLabel(validity: ValidityCheck): string {
  return validity.enabled ? "Active" : "Stored, not active";
}

export function validityThresholdSentence(validity: ValidityCheck): string {
  return `Fires at ${validity.threshold} across the summed indicator items.`;
}

/** A questionnaire that stores no screen at all — a fact, not a failed read. */
export const NO_VALIDITY_SENTENCE =
  "None — this instrument carries no malingering screen.";

export function itemPath(index: number): string {
  return `content.items[${index}]`;
}

export function optionPath(index: number): string {
  return `content.responseScale[${index}]`;
}

export function bandPath(index: number): string {
  return `content.severityBands[${index}]`;
}

export const LANGUAGE_PATH = "content.language";
export const INSTRUCTIONS_PATH = "content.instructions";
export const ITEMS_PATH = "content.items";
export const SCALE_PATH = "content.responseScale";
export const SCORED_PATH = "content.scored";
export const SCORING_METHOD_PATH = "content.scoringMethod";
export const BANDS_PATH = "content.severityBands";
export const VALIDITY_PATH = "content.validity";
export const VALIDITY_THRESHOLD_PATH = "content.validity.threshold";
export const VALIDITY_INDICATORS_PATH = "content.validity.indicatorItemIds";

export function newDraftItem(): DraftQuestionnaireItem {
  return { id: newDraftId("item"), itemId: "", text: "", reverseScored: false };
}

export function newDraftOption(): DraftResponseOption {
  return { id: newDraftId("option"), value: "", label: "" };
}

export function newDraftBand(): DraftSeverityBand {
  return {
    id: newDraftId("band"),
    name: "",
    scoreFrom: "0",
    scoreTo: "0",
    severityLabel: "",
  };
}

/** A blank instrument, for authoring one from scratch. */
export function emptyQuestionnaireDraft(): DraftQuestionnaire {
  return {
    language: "en",
    instructions: "",
    items: [newDraftItem()],
    // The contract's floor is two points, because a one-point scale records no
    // answer. Starting at the floor beats starting at one and refusing on Save.
    responseScale: [newDraftOption(), newDraftOption()],
    scored: true,
    scoringMethod: "sum",
    severityBands: [],
    validity: { present: false, enabled: true, threshold: "0", indicatorItemIds: [] },
  };
}

/** The stored content, opened for editing. */
export function toQuestionnaireDraft(
  content: QuestionnaireContent,
): DraftQuestionnaire {
  return {
    language: content.language,
    instructions: content.instructions ?? "",
    items: content.items.map((item) => ({
      id: newDraftId("item"),
      itemId: item.id,
      text: item.text,
      reverseScored: item.reverseScored ?? false,
    })),
    responseScale: content.responseScale.map((option) => ({
      id: newDraftId("option"),
      value: String(option.value),
      label: option.label,
    })),
    scored: content.scored,
    // Absent means sum, which is the reading the service documents on the
    // field. A questionnaire stored before #368 opens as a sum-scored one.
    scoringMethod: content.scoringMethod ?? "sum",
    severityBands: (content.severityBands ?? []).map((band) => ({
      id: newDraftId("band"),
      name: band.name,
      scoreFrom: String(band.scoreFrom),
      scoreTo: String(band.scoreTo),
      severityLabel: band.severityLabel,
    })),
    validity: content.validity
      ? {
          present: true,
          enabled: content.validity.enabled,
          threshold: String(content.validity.threshold),
          indicatorItemIds: [...content.validity.indicatorItemIds],
        }
      : {
          present: false,
          enabled: true,
          threshold: "0",
          indicatorItemIds: [],
        },
  };
}

/**
 * The largest magnitude a stored integer may carry: `Number.MAX_SAFE_INTEGER`.
 *
 * The service refuses anything beyond it outright (`_JS_SAFE_INTEGER` in
 * `library.py`), and beyond it a decimal string and the double it parses to are
 * no longer the same number.
 */
export const MAX_SAFE_STORED_INTEGER = Number.MAX_SAFE_INTEGER;

function parseInteger(value: string): number | null {
  const trimmed = value.trim();
  if (!/^-?\d+$/.test(trimmed)) {
    return null;
  }
  const parsed = Number.parseInt(trimmed, 10);
  // `parseInt` silently rounds past 2^53: "9007199254740993" becomes
  // 9007199254740992. Accepting that would publish a number the clinician never
  // typed into content that can never be edited again — and the service would
  // refuse the rounded value anyway, so the only thing an unguarded parse buys
  // is a 422 that names no field. Refuse it here, where the field is known.
  return Number.isSafeInteger(parsed) ? parsed : null;
}

/**
 * The response-scale values that currently parse, in authored order.
 *
 * Exported because the editor renders the reverse-key mapping from them, and a
 * second parse of the same buffers is a second answer to "which values does
 * this scale have" — the derived range and the mapping beside a toggle must not
 * be able to disagree.
 */
export function parsedScaleValues(draft: DraftQuestionnaire): number[] {
  return draft.responseScale
    .map((option) => parseInteger(option.value))
    .filter((value): value is number => value !== null);
}

/**
 * What one reverse-keyed response contributes: `(scaleMin + scaleMax) - value`.
 *
 * Derived from the instrument's own scale rather than stored, which is what
 * makes the flag the whole persistence footprint. Exported because the editor
 * SHOWS the mapping beside the toggle — "0 → 3, 1 → 2, …" — and a clinician
 * cannot check a re-keying they cannot see. It describes the instrument; it
 * does not score anybody's answers.
 */
export function reversedContribution(
  value: number,
  scaleMin: number,
  scaleMax: number,
): number {
  return scaleMin + scaleMax - value;
}

/**
 * The range severity bands are checked against, or `null` when the scale is not
 * yet parseable.
 *
 * The exact derivation `QuestionnaireContent._coherent` runs. A SUM ranges over
 * the whole instrument; a MEAN cannot leave the response scale it averages.
 * Reverse scoring does not enter it: both ends of the scale are attainable, so
 * `(scaleMin + scaleMax) - value` maps the scale's maximum onto its minimum and
 * back, leaving the derived minimum and maximum exactly where they were.
 */
export function derivedScoreRange(
  draft: DraftQuestionnaire,
): { lowest: number; highest: number } | null {
  const values = parsedScaleValues(draft);
  if (values.length < MIN_SCALE_OPTIONS || draft.items.length === 0) {
    return null;
  }
  const lowest = Math.min(...values);
  const highest = Math.max(...values);
  if (draft.scoringMethod === "mean") {
    return { lowest, highest };
  }
  return {
    lowest: draft.items.length * lowest,
    highest: draft.items.length * highest,
  };
}

/**
 * Every non-blocking warning the draft raises, with its control.
 *
 * One today: a score the bands do not cover. The service permits interior gaps
 * on purpose — partial banding is legitimate authoring, and the seeded PHQ-9
 * documents exactly that — so this can never be a refusal without making the
 * editor stricter than the contract it writes to. It is still worth saying: a
 * clinician who meant to stratify everything has no other way to notice the
 * hole, and the version they publish is immutable.
 */
export function findQuestionnaireWarnings(
  draft: DraftQuestionnaire,
): QuestionnaireProblem[] {
  const warnings: QuestionnaireProblem[] = [];
  if (!draft.scored || draft.severityBands.length === 0) {
    return warnings;
  }
  const range = derivedScoreRange(draft);
  if (range === null) {
    return warnings;
  }

  const bands = draft.severityBands
    .map((band) => ({
      from: parseInteger(band.scoreFrom),
      to: parseInteger(band.scoreTo),
    }))
    .filter(
      (band): band is { from: number; to: number } =>
        band.from !== null && band.to !== null && band.from <= band.to,
    )
    .sort((left, right) => left.from - right.from);
  if (bands.length !== draft.severityBands.length) {
    // Some band is mid-edit or already a blocking problem; a coverage claim
    // computed from a partial set would name gaps that are not there.
    return warnings;
  }
  if (bands.some((band) => band.from < range.lowest || band.to > range.highest)) {
    // A band outside the derived range is already refused, and the range it
    // would be measured against is exactly what is in dispute — so a coverage
    // claim here would name scores the instrument cannot reach as uncovered,
    // beside the refusal that says so.
    return warnings;
  }

  // COVERAGE IS CLAIMED AT INTEGER GRANULARITY, under both scoring methods, and
  // that is a decision rather than an oversight about fractional means.
  //
  // `SeverityBand.scoreFrom/scoreTo` are integers (`int, ge=0` on the wire), so
  // no band boundary below integer granularity can be authored at all, and the
  // two bands that would straddle one — 0-1 and 1-2 — are refused as
  // overlapping. Consecutive integer bands are therefore CUT POINTS, and
  // stepping the cursor to `band.to + 1` is what treats them as such.
  //
  // Under `mean` a fractional score is reachable, and reading these bands as
  // closed real intervals instead would make the warning unclearable: a 9-item
  // 0-3 instrument banded 0-0/1-1/2-2/3-3 reaches 28 means, 24 of them
  // fractional, and no authoring action could remove them from an "uncovered"
  // list — integer bounds cannot tile a continuous range. Which band a
  // fractional mean resolves to is the runtime mapping #370 owns; this function
  // reports holes a clinician can actually close, and does not invent it.
  const uncovered: string[] = [];
  let cursor = range.lowest;
  for (const band of bands) {
    if (band.from > cursor) {
      uncovered.push(band.from - 1 === cursor ? `${cursor}` : `${cursor}-${band.from - 1}`);
    }
    cursor = Math.max(cursor, band.to + 1);
  }
  if (cursor <= range.highest) {
    uncovered.push(
      cursor === range.highest ? `${cursor}` : `${cursor}-${range.highest}`,
    );
  }

  if (uncovered.length > 0) {
    warnings.push({
      path: BANDS_PATH,
      message: `No band covers ${uncovered.join(", ")}. A score there is stratified as nothing, which is legitimate for an instrument that only flags elevated ranges — and a hole for one that stratifies everything.`,
    });
  }
  return warnings;
}

/**
 * Every blocking problem in the draft, each attributed to the control it is
 * about.
 *
 * Ordered the way a clinician reads the surface — the instrument's own
 * metadata, then the items, then the response scale, then the bands, then the
 * validity screen, whose checks only make sense once the items exist.
 */
export function findQuestionnaireProblems(
  draft: DraftQuestionnaire,
): QuestionnaireProblem[] {
  const problems: QuestionnaireProblem[] = [];

  if (draft.language.trim().length === 0) {
    problems.push({
      path: LANGUAGE_PATH,
      message: "A questionnaire records the language its items are written in.",
    });
  } else if (!LANGUAGE_PATTERN.test(draft.language.trim())) {
    problems.push({
      path: LANGUAGE_PATH,
      message: 'A language is a two-letter code, optionally with a region — "en" or "en-GB".',
    });
  }

  if (draft.instructions.length > MAX_TEXT_CHARS) {
    problems.push({
      path: INSTRUCTIONS_PATH,
      message: `The administration stem is at most ${MAX_TEXT_CHARS.toLocaleString("en-US")} characters; this one is ${draft.instructions.length.toLocaleString("en-US")}.`,
    });
  }

  if (draft.items.length < MIN_ITEMS) {
    problems.push({
      path: ITEMS_PATH,
      message: "A questionnaire needs at least one item.",
    });
  }
  if (draft.items.length > MAX_ITEMS) {
    problems.push({
      path: ITEMS_PATH,
      message: `A questionnaire carries at most ${MAX_ITEMS} items.`,
    });
  }

  const seenItemIds = new Map<string, number>();
  draft.items.forEach((item, index) => {
    const path = itemPath(index);
    if (item.itemId.length === 0) {
      problems.push({ path, message: "This item needs an id." });
    } else if (!ITEM_ID_PATTERN.test(item.itemId)) {
      problems.push({
        path,
        message:
          'An item id is a lowercase slug — "q1", "sleep_quality" — of at most 64 characters.',
      });
    } else {
      const first = seenItemIds.get(item.itemId);
      if (first === undefined) {
        seenItemIds.set(item.itemId, index);
      } else {
        problems.push({
          path,
          message: `Item ids must be unique; this id is already used by item ${first + 1}.`,
        });
      }
    }

    if (item.text.trim().length === 0) {
      problems.push({ path, message: "This item needs its wording." });
    } else if (item.text.length > MAX_TEXT_CHARS) {
      problems.push({
        path,
        message: `Item wording is at most ${MAX_TEXT_CHARS.toLocaleString("en-US")} characters.`,
      });
    }
  });

  if (draft.responseScale.length < MIN_SCALE_OPTIONS) {
    problems.push({
      path: SCALE_PATH,
      message: `A response scale needs at least ${MIN_SCALE_OPTIONS} points; one point records no answer.`,
    });
  }
  if (draft.responseScale.length > MAX_SCALE_OPTIONS) {
    problems.push({
      path: SCALE_PATH,
      message: `A response scale carries at most ${MAX_SCALE_OPTIONS} points.`,
    });
  }

  const seenValues = new Map<number, number>();
  draft.responseScale.forEach((option, index) => {
    const path = optionPath(index);
    const value = parseInteger(option.value);
    if (value === null) {
      problems.push({
        path,
        message: `A response value is a whole number no larger than ${MAX_SAFE_STORED_INTEGER}.`,
      });
    } else {
      const first = seenValues.get(value);
      if (first === undefined) {
        seenValues.set(value, index);
      } else {
        problems.push({
          path,
          message: `Response values must be unique; ${value} is already used by point ${first + 1}.`,
        });
      }
    }
    if (option.label.trim().length === 0) {
      problems.push({ path, message: "This response point needs a label." });
    } else if (option.label.length > MAX_TEXT_CHARS) {
      problems.push({
        path,
        message: `A response label is at most ${MAX_TEXT_CHARS.toLocaleString("en-US")} characters.`,
      });
    }
  });

  // The scoring model, and the two states an unscored instrument refuses. Both
  // are authoring slips that would sit in immutable content looking configured
  // — the same class as a band on an unscored instrument, which the service has
  // refused since #34.
  if (!draft.scored && draft.severityBands.length > 0) {
    problems.push({
      path: BANDS_PATH,
      message:
        "An unscored questionnaire cannot carry severity bands: there is no score to stratify. Mark it scored, or remove the bands.",
    });
  }
  if (!draft.scored && draft.scoringMethod !== "sum") {
    problems.push({
      path: SCORING_METHOD_PATH,
      message:
        "An unscored questionnaire computes nothing, so it cannot carry a scoring method.",
    });
  }
  if (!draft.scored && draft.items.some((item) => item.reverseScored)) {
    problems.push({
      path: ITEMS_PATH,
      message:
        "An unscored questionnaire cannot reverse-score an item: reversing changes a contribution to a score it does not compute.",
    });
  }

  if (draft.severityBands.length > MAX_BANDS) {
    problems.push({
      path: BANDS_PATH,
      message: `A questionnaire carries at most ${MAX_BANDS} severity bands.`,
    });
  }

  const range = derivedScoreRange(draft);
  const parsedBands: { index: number; from: number; to: number }[] = [];
  draft.severityBands.forEach((band, index) => {
    const path = bandPath(index);
    if (band.name.trim().length === 0) {
      problems.push({ path, message: "This band needs a name." });
    } else if (band.name.length > MAX_NAME_CHARS) {
      problems.push({
        path,
        message: `A band name is at most ${MAX_NAME_CHARS} characters.`,
      });
    }
    if (band.severityLabel.trim().length === 0) {
      problems.push({
        path,
        message:
          "This band needs a severity label — what the runtime emits when a score lands in it.",
      });
    } else if (band.severityLabel.length > MAX_NAME_CHARS) {
      problems.push({
        path,
        message: `A severity label is at most ${MAX_NAME_CHARS} characters.`,
      });
    }

    const from = parseInteger(band.scoreFrom);
    const to = parseInteger(band.scoreTo);
    if (from === null || to === null || from < 0 || to < 0) {
      problems.push({
        path,
        message: `A band's bounds are whole numbers between 0 and ${MAX_SAFE_STORED_INTEGER}.`,
      });
      return;
    }
    if (from > to) {
      problems.push({
        path,
        message: `This band runs backwards: ${from} to ${to}.`,
      });
      return;
    }
    if (range && (from < range.lowest || to > range.highest)) {
      problems.push({
        path,
        message:
          draft.scoringMethod === "mean"
            ? `A mean cannot leave the response scale it averages, so bands run ${range.lowest}–${range.highest}. This one is ${from}–${to}.`
            : `${draft.items.length} items on this scale can total ${range.lowest}–${range.highest}, so no score reaches ${from}–${to}.`,
      });
      return;
    }
    parsedBands.push({ index, from, to });
  });

  // Overlap, on the LATER band of each pair: the clinician reads downwards, and
  // the second band is the one whose start collides.
  const ordered = [...parsedBands].sort((left, right) => left.from - right.from);
  for (let position = 1; position < ordered.length; position += 1) {
    const previous = ordered[position - 1] as { index: number; from: number; to: number };
    const band = ordered[position] as { index: number; from: number; to: number };
    if (band.from <= previous.to) {
      problems.push({
        path: bandPath(band.index),
        message: `Severity bands must not overlap: ${band.from}–${band.to} runs into ${previous.from}–${previous.to}, so one score would carry two severities.`,
      });
    }
  }

  if (draft.validity.present) {
    const knownIds = new Set(
      draft.items.map((item) => item.itemId).filter((id) => id.length > 0),
    );
    const indicators = draft.validity.indicatorItemIds;
    if (indicators.length === 0) {
      problems.push({
        path: VALIDITY_INDICATORS_PATH,
        message:
          "A validity screen needs at least one indicator item; a screen over nothing can never fire.",
      });
    }
    if (indicators.length > MAX_INDICATORS) {
      problems.push({
        path: VALIDITY_INDICATORS_PATH,
        message: `A validity screen carries at most ${MAX_INDICATORS} indicator items.`,
      });
    }
    if (new Set(indicators).size !== indicators.length) {
      problems.push({
        path: VALIDITY_INDICATORS_PATH,
        message:
          "Indicator items must be unique; a duplicate would be double-counted by a runtime summing the list.",
      });
    }
    const unknown = [...new Set(indicators)].filter((id) => !knownIds.has(id)).sort();
    if (unknown.length > 0) {
      problems.push({
        path: VALIDITY_INDICATORS_PATH,
        message: `These indicators are not items of this questionnaire: ${unknown.join(", ")}.`,
      });
    }

    const threshold = parseInteger(draft.validity.threshold);
    if (threshold === null || threshold < 0) {
      problems.push({
        path: VALIDITY_THRESHOLD_PATH,
        message: `A validity threshold is a whole number between 0 and ${MAX_SAFE_STORED_INTEGER}.`,
      });
    } else {
      const values = parsedScaleValues(draft);
      const reachable =
        values.length >= MIN_SCALE_OPTIONS && unknown.length === 0
          ? new Set(indicators).size * Math.max(...values)
          : null;
      // Bounded by the INDICATOR items, not the whole instrument: the screen
      // sums only those, so a threshold above their total can never be reached
      // and the screen is silently disabled. `scoringMethod` does not move this
      // — the screen sums its indicators whatever the instrument's own score is.
      if (reachable !== null && threshold > reachable) {
        problems.push({
          path: VALIDITY_THRESHOLD_PATH,
          message: `The indicator items can total at most ${reachable}, so a threshold of ${threshold} can never be reached and the screen would never fire.`,
        });
      }
    }
  }

  return problems;
}

/**
 * The draft as wire content, or the reasons it is not sendable yet.
 *
 * Never partially converts: a caller either gets content it may send or a list
 * of problems it may attribute, so no path exists that publishes half an
 * instrument.
 *
 * BOTH SCORING FIELDS ARE OMITTED AT THEIR DEFAULT. A sum-scored instrument
 * sends no `scoringMethod` and an item that is not re-keyed sends no
 * `reverseScored`, so a questionnaire that uses neither stores and hashes
 * exactly as it did before #368 — an editor that padded every document with its
 * defaults would change the identity of content nobody meant to change.
 */
export function toQuestionnaireContent(
  draft: DraftQuestionnaire,
):
  | { ok: true; content: QuestionnaireContent }
  | { ok: false; problems: QuestionnaireProblem[] } {
  const problems = findQuestionnaireProblems(draft);
  if (problems.length > 0) {
    return { ok: false, problems };
  }

  const instructions = draft.instructions.trim();
  return {
    ok: true,
    content: {
      kind: "questionnaire",
      language: draft.language.trim(),
      instructions: instructions === "" ? null : instructions,
      items: draft.items.map((item) => ({
        id: item.itemId,
        text: item.text.trim(),
        ...(item.reverseScored ? { reverseScored: true } : {}),
      })),
      responseScale: draft.responseScale.map((option) => ({
        value: parseInteger(option.value) as number,
        label: option.label.trim(),
      })),
      scored: draft.scored,
      ...(draft.scored && draft.scoringMethod !== "sum"
        ? { scoringMethod: draft.scoringMethod }
        : {}),
      severityBands: draft.severityBands.map((band) => ({
        name: band.name.trim(),
        scoreFrom: parseInteger(band.scoreFrom) as number,
        scoreTo: parseInteger(band.scoreTo) as number,
        severityLabel: band.severityLabel.trim(),
      })),
      validity: draft.validity.present
        ? {
            enabled: draft.validity.enabled,
            threshold: parseInteger(draft.validity.threshold) as number,
            indicatorItemIds: [...draft.validity.indicatorItemIds],
          }
        : null,
    },
  };
}

/**
 * Dirty is decided on what the clinician sees, not on what it would serialise
 * to — the same rule `lib/alertMatrix.ts` and `lib/assetContent.ts` apply.
 */
export function isSameQuestionnaireDraft(
  left: DraftQuestionnaire,
  right: DraftQuestionnaire,
): boolean {
  return (
    left.language === right.language &&
    left.instructions === right.instructions &&
    left.scored === right.scored &&
    left.scoringMethod === right.scoringMethod &&
    left.items.length === right.items.length &&
    left.responseScale.length === right.responseScale.length &&
    left.severityBands.length === right.severityBands.length &&
    left.items.every((item, index) => {
      const other = right.items[index] as DraftQuestionnaireItem;
      return (
        item.itemId === other.itemId &&
        item.text === other.text &&
        item.reverseScored === other.reverseScored
      );
    }) &&
    left.responseScale.every((option, index) => {
      const other = right.responseScale[index] as DraftResponseOption;
      return option.value === other.value && option.label === other.label;
    }) &&
    left.severityBands.every((band, index) => {
      const other = right.severityBands[index] as DraftSeverityBand;
      return (
        band.name === other.name &&
        band.scoreFrom === other.scoreFrom &&
        band.scoreTo === other.scoreTo &&
        band.severityLabel === other.severityLabel
      );
    }) &&
    left.validity.present === right.validity.present &&
    left.validity.enabled === right.validity.enabled &&
    left.validity.threshold === right.validity.threshold &&
    left.validity.indicatorItemIds.join(",") ===
      right.validity.indicatorItemIds.join(",")
  );
}

/**
 * Which control a server refusal is about.
 *
 * The service points every questionnaire failure at `content`, which is correct
 * and too coarse to attribute. Its sentences are written around vocabulary the
 * SERVER defined — its own field names and its own derived numbers — so this
 * reads structure rather than prose, and resolves to `null` whenever it cannot
 * be sure. A refusal attached to the wrong control is worse than one attached
 * to the instrument.
 */
export function attributeQuestionnaireFailure(message: string): string | null {
  if (/validity|indicator|threshold/i.test(message)) {
    return /threshold/i.test(message) && !/indicator items are not/i.test(message)
      ? VALIDITY_THRESHOLD_PATH
      : VALIDITY_INDICATORS_PATH;
  }
  if (/scoring method/i.test(message)) {
    return SCORING_METHOD_PATH;
  }
  if (/reverse-score/i.test(message)) {
    return ITEMS_PATH;
  }
  if (/\bband/i.test(message)) {
    return BANDS_PATH;
  }
  if (/response scale/i.test(message)) {
    return SCALE_PATH;
  }
  if (/\bitem ids\b/i.test(message)) {
    return ITEMS_PATH;
  }
  return null;
}
